<template>
  <div>
    <v-row>
      <v-col cols="3" md="3">
        <v-select
          v-model="selectedTaxYear"
          :items="taxYearOptions"
          label="Tax Year"
          outlined
          dense
          @change="handleTaxYearChange"
        >
          <template v-slot:selection="{ item }">
            {{ item.text }}
          </template>
          <template v-slot:item="{ item }">
            {{ item.text }}
          </template>
        </v-select></v-col
      >
      <v-col cols="3" md="3"
        ><v-select
          v-model="exportFormat"
          :items="exportFomatsList"
          label="Export Format"
          outlined
          dense
        ></v-select
      ></v-col>
      <v-col cols="3" md="3">
        <v-btn
          color="primary"
          @click="emitExport()"
          :disabled="!isDateSelected"
        >
          Export
        </v-btn></v-col
      >
    </v-row>

    <!-- Custom Tax Year Dialog -->
    <v-dialog v-model="customDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon left>mdi-calendar-range</v-icon>
          Custom Tax Year
        </v-card-title>

        <v-card-text>
          <p class="mb-4">Select the ending date for your custom tax year:</p>

          <v-date-picker
            v-model="customEndDate"
            :max="maxCustomDate"
            :min="minCustomDate"
            full-width
            color="primary"
            show-current
          ></v-date-picker>

          <v-alert v-if="customEndDate" type="info" outlined class="mt-4">
            <strong>Selected Tax Year:</strong><br />
            {{ formatCustomDateRange(customEndDate) }}
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelCustomSelection"> Cancel </v-btn>
          <v-btn
            color="primary"
            :disabled="!customEndDate"
            @click="confirmCustomSelection"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { AppSetup } from "@/store/sitedata";
export default {
  name: "TaxYearSelector",

  props: ["exportFomatsList"],

  data() {
    return {
      selectedTaxYear: null,
      customDialog: false,
      customEndDate: null,
      previousSelectedYear: null,
      exportFormat: null,
      // exportFomatsList: ["CSV", "PDF", "Excel", "Quickbooks", "Sage", "Xero"],
      //   exportFomatsList: ["CSV", "PDF", "Excel", "Quickbooks", "Sage", "Xero"],

      // Configuration
      earliestTaxYear: AppSetup.startYear, //2010, // Fixed date - can go back to 2010-2011 tax year
      maxCustomDate: new Date().toISOString().substr(0, 10), // Today
      minCustomDate: AppSetup.startDate, //"2010-04-06", // Earliest possible custom date
    };
  },

  computed: {
    currentTaxYear() {
      const now = new Date();
      const currentYear = now.getFullYear();
      const taxYearStart = new Date(currentYear, 3, 6); // April 6th

      // If we're before April 6th, we're still in the previous tax year
      if (now < taxYearStart) {
        return currentYear - 1;
      }
      return currentYear;
    },

    taxYearOptions() {
      const options = [];
      const current = this.currentTaxYear;

      // Add current and previous tax years
      for (let year = current; year >= this.earliestTaxYear; year--) {
        const startDate = new Date(year, 3, 6); // April 6th
        const endDate = new Date(year + 1, 3, 5); // April 5th next year

        options.push({
          text: `${year}/${(year + 1).toString().substr(2)} (${this.formatDate(
            startDate
          )} - ${this.formatDate(endDate)})`,
          value: year,
          startDate: startDate,
          endDate: endDate,
          isCurrent: year === current,
        });
      }

      // Add custom option
      options.push({
        text: "Custom Tax Year...",
        value: "custom",
        startDate: null,
        endDate: null,
        isCurrent: false,
      });

      return options;
    },
    isDateSelected() {
      if (this.exportFormat) {
        if (this.selectedTaxYear == "custom") {
          if (this.customEndDate) {
            return true;
          }
          return false;
        }
        return true;
      }
      return false;
    },
  },

  methods: {
    handleTaxYearChange(selectedValue) {
      if (selectedValue === "custom") {
        this.openCustomDialog();
      } else {
        this.emitTaxYearRange(selectedValue);
      }
    },

    openCustomDialog() {
      this.customDialog = true;
      this.customEndDate = null;
    },

    cancelCustomSelection() {
      this.customDialog = false;
      this.customEndDate = null;
      // Revert to previous selection
      this.selectedTaxYear = this.previousSelectedYear;
    },

    confirmCustomSelection() {
      if (this.customEndDate) {
        const endDate = new Date(this.customEndDate);
        const startDate = new Date(endDate);
        startDate.setFullYear(startDate.getFullYear() - 1);
        startDate.setDate(startDate.getDate() + 1); // Start day after end date minus 365 days

        const customRange = {
          startDate: startDate,
          endDate: endDate,
          isCustom: true,
          label: this.formatCustomDateRange(this.customEndDate),
        };

        this.emitCustomRange(customRange);
        this.customDialog = false;
      }
    },

    emitTaxYearRange(taxYear) {
      this.previousSelectedYear = taxYear;
      const selectedOption = this.taxYearOptions.find(
        (option) => option.value === taxYear
      );

      if (selectedOption) {
        const range = {
          startDate: selectedOption.startDate,
          endDate: selectedOption.endDate,
          taxYear: taxYear,
          isCustom: false,
          isCurrent: selectedOption.isCurrent,
          label: selectedOption.text,
        };

        this.$emit("tax-year-changed", range);
      }
    },

    emitCustomRange(customRange) {
      this.$emit("tax-year-changed", customRange);
    },

    formatDate(date) {
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    formatCustomDateRange(endDateString) {
      const endDate = new Date(endDateString);
      const startDate = new Date(endDate);
      startDate.setFullYear(startDate.getFullYear() - 1);
      startDate.setDate(startDate.getDate() + 1);

      return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;
    },

    setDefaultTaxYear() {
      this.selectedTaxYear = this.currentTaxYear;
      this.emitTaxYearRange(this.currentTaxYear);
    },
    emitExport() {
      let s_date = "",
        e_date = "",
        exp_format = this.exportFormat;

      if (this.selectedTaxYear == "custom") {
        //console.log("custome end date", this.customEndDate);

        e_date = this.customEndDate; // already got month and day
        s_date = this.yearDeductor(e_date);
      } else {
        e_date = this.selectedTaxYear + 1; // make sure top year is selected
        e_date += "-04-05"; // convert to string date

        s_date = this.yearDeductor(e_date);
      }
      this.$emit("export", {
        start_date: s_date,
        end_date: e_date,
        exp_format: exp_format,
      });
    },
    yearDeductor(year_num) {
      if (!year_num) {
        return null;
      } else {
        const end_date = new Date(year_num);
        end_date.setFullYear(end_date.getFullYear() - 1);
        end_date.setDate(end_date.getDate() + 1); // Start day after end date minus 365 days
        return end_date.toISOString().slice(0, 10);
      }
    },
  },

  mounted() {
    // Emit current tax year on component load
    this.setDefaultTaxYear();
  },
};
</script>

<style scoped>
.headline {
  word-break: normal;
}
</style>
